'use client'


import { useCookieConsent } from '@/hooks/use-cookie-consent'
import { getLocalePrefix, toPublicLocale } from '@/lib/locale-prefix'
import { Button } from '@/components/ui/button'
import { Cookie } from 'lucide-react'
import Link from 'next/link'
import { useTranslations, useLocale } from 'next-intl'

/**
 * Banner de consentimiento de cookies conforme a GDPR
 * Se muestra en la parte inferior de la pantalla hasta que el usuario dé su consentimiento
 */
export function CookieConsentBanner() {
  const { consentStatus, isLoading, acceptAll, rejectAll } = useCookieConsent()
  const t = useTranslations('common.cookies')
  const locale = useLocale()
  const privacyLink = `${getLocalePrefix(toPublicLocale(locale))}/privacy`

  const isVisible = !isLoading && consentStatus === 'pending'

  if (!isVisible) {
    return null
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
    >
      <div className="mx-auto max-w-7xl px-3 py-3 sm:px-6 sm:py-4 lg:px-8">
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
          {/* Icono y contenido */}
          <div className="flex min-w-0 flex-1 items-start gap-2 sm:gap-3">
            <div className="mt-1 flex-shrink-0">
              <Cookie className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h3
                id="cookie-consent-title"
                className="mb-1 text-base font-semibold text-gray-900 dark:text-white"
              >
                {t('title')}
              </h3>
              <p
                id="cookie-consent-description"
                className="text-sm leading-5 text-gray-600 dark:text-gray-300"
              >
                {t('description')}{' '}
                <Link
                  href={privacyLink}
                  className="text-red-600 dark:text-red-400 hover:underline font-medium"
                >
                  {t('moreInfo')}
                </Link>
              </p>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex w-full flex-row gap-2 sm:w-auto sm:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={rejectAll}
              className="w-full flex-1 sm:w-auto sm:flex-none"
              aria-label={t('rejectAriaLabel')}
            >
              {t('reject')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={acceptAll}
              className="w-full flex-1 bg-red-600 text-white hover:bg-red-700 sm:w-auto sm:flex-none"
              aria-label={t('acceptAriaLabel')}
            >
              {t('acceptAll')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
